import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { fieldSx } from "../../egresos/components/formStyles";
import usePaginado from "../../egresos/components/usePaginado";
import ListadoTabla from "../../egresos/components/ListadoTabla";
import FacturaManualDialog from "../components/FacturaManualDialog";
import { anularFacturaManual, buscarServiciosParaFacturar, getCatalogosFacturaManual, listarFacturasManuales } from "../services/facturaManualService";

const AZUL = "#005594";
const dinero = (n) => Number(n || 0).toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Cabecera = ({ children, align }) => (
  <TableCell align={align} sx={{ fontWeight: 800, fontSize: 11, whiteSpace: "nowrap", color: AZUL, bgcolor: `${AZUL}1a`, borderBottom: `2px solid ${AZUL}` }}>{children}</TableCell>
);

// Registro manual de facturas: se busca al cliente (servicio activo) y desde ahí se factura, igual que en Facturación.
// La factura se emitió fuera del sistema: aquí solo se registra en la base de datos (sin SIAT ni PDF).
export default function FacturaManualPage() {
  const [buscar, setBuscar] = useState("");
  const [servicios, setServicios] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [servicioElegido, setServicioElegido] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [cat, setCat] = useState(null);

  useEffect(() => {
    getCatalogosFacturaManual()
      .then((r) => setCat(r.data))
      .catch(() => setAviso({ tipo: "error", texto: "No se pudieron cargar los datos del formulario." }));
  }, []);

  const listado = usePaginado((p) => listarFacturasManuales({ ...p, buscar: filtro }), [filtro]);

  const handleBuscar = async () => {
    if (!buscar.trim()) return;
    setBuscando(true);
    setAviso(null);
    try {
      const res = await buscarServiciosParaFacturar(buscar.trim());
      const data = res.data?.data ?? res.data;
      setServicios(Array.isArray(data) ? data : []);
      setBuscado(true);
    } catch (e) {
      setAviso({ tipo: "error", texto: e?.response?.data?.message || "Error al buscar" });
    } finally {
      setBuscando(false);
    }
  };

  const registrada = (r) => {
    setServicioElegido(null);
    setAviso({ tipo: "success", texto: `Factura N° ${r?.num_fac ?? ""} registrada${r?.monto_total != null ? ` (${dinero(r.monto_total)} Bs)` : ""}.` });
    listado.recargar(true);
  };

  const anular = async (fila) => {
    if (!window.confirm(`¿Anular la factura N° ${fila.num_fac}? Esta acción no se puede deshacer.`)) return;
    try {
      await anularFacturaManual(fila.cod);
      setAviso({ tipo: "success", texto: "Factura anulada." });
      listado.recargar();
    } catch (e) {
      setAviso({ tipo: "error", texto: e?.response?.data?.error || "No se pudo anular la factura." });
    }
  };

  const columnas = [
    { h: "N°", render: (x) => x.num_fac },
    { h: "Fecha", render: (x) => String(x.fecha_fac || "").slice(0, 10) },
    { h: "Sucursal", render: (x) => x.sucursal },
    { h: "NIT", render: (x) => x.nit },
    { h: "Nombre", render: (x) => x.fac_Nombre },
    { h: "Monto (Bs)", align: "right", render: (x) => dinero(x.monto_total) },
    { h: "Estado", render: (x) => (x.estado === "X" ? "Anulada" : x.pagada === "si" ? "Pagada" : Number(x.pagos) > 0 ? "Con pagos" : "Vigente") },
    {
      h: "", align: "right",
      render: (x) => (x.estado !== "X" && Number(x.pagos) === 0
        ? <Button size="small" color="error" sx={{ minWidth: 0, py: 0, fontSize: 11 }} onClick={() => anular(x)}>Anular</Button> : null),
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader title="Registrar factura" showBack={false} />
      <Box sx={{ p: 1.5, maxWidth: 1250, mx: "auto" }}>
        <Alert severity="info" sx={{ py: 0, mb: 1, fontSize: 12 }}>
          Busca al cliente y registra su factura. Aquí solo se guardan los datos de una factura emitida fuera del sistema (para reportes y cobros): no se envía al SIAT ni se genera PDF.
        </Alert>
        {aviso && <Alert severity={aviso.tipo} onClose={() => setAviso(null)} sx={{ py: 0, mb: 1 }}>{aviso.texto}</Alert>}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            size="small" label="Buscar por Razón Social" value={buscar} onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }} sx={{ ...fieldSx, width: { xs: "100%", sm: 280 } }}
            slotProps={{ input: { endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleBuscar} disabled={buscando}><SearchIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) } }}
          />
          <Button size="small" variant="contained" onClick={handleBuscar} disabled={buscando || !buscar.trim()}
            startIcon={buscando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} sx={{ minHeight: 28, bgcolor: AZUL, "&:hover": { bgcolor: "#003f6e" } }}>
            Buscar
          </Button>
        </Box>

        {buscado && (servicios.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>No se encontraron servicios activos para "{buscar}".</Alert>
        ) : (
          <Paper variant="outlined" sx={{ mb: 1 }}>
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow>
                    <Cabecera>COD Servicio</Cabecera><Cabecera>Razón Social</Cabecera><Cabecera>Grupo</Cabecera><Cabecera>Nombre servicio</Cabecera>
                    <Cabecera>Servicio</Cabecera><Cabecera>Tipo</Cabecera><Cabecera>Sucursal</Cabecera><Cabecera>Descripción</Cabecera><Cabecera align="center">Acción</Cabecera>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servicios.map((s) => (
                    <TableRow key={s.cod_servicio_cliente} hover>
                      <TableCell>{s.cod_servicio_cliente}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={500}>{s.razon_social}</Typography></TableCell>
                      <TableCell>{s.grupo || "-"}</TableCell>
                      <TableCell>{s.cliente_servicio_nombre || "-"}</TableCell>
                      <TableCell>{s.servicio_nombre}</TableCell>
                      <TableCell>{s.tipo}</TableCell>
                      <TableCell>{s.sucursal_facturar}</TableCell>
                      <TableCell>{s.descripcion}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" startIcon={<ReceiptIcon />} sx={{ minHeight: 28, fontSize: "0.72rem", px: 0.75, color: AZUL, borderColor: AZUL }}
                          disabled={!cat} onClick={() => setServicioElegido(s)}>
                          Registrar factura
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: -0.5 }}>
          <TextField size="small" placeholder="Filtrar por nombre, NIT o N°" value={filtro} sx={{ ...fieldSx, width: 250 }}
            onChange={(e) => { setFiltro(e.target.value); listado.setPage(1); }} />
        </Box>
        <ListadoTabla
          titulo="FACTURAS REGISTRADAS" color={AZUL} columnas={columnas} paginado={listado} cargando={listado.cargando}
          filas={listado.items.map((x) => ({ ...x, id: x.cod }))} vacio="Aún no hay facturas registradas."
        />
        {listado.error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{listado.error}</Alert>}
      </Box>

      {servicioElegido && cat && (
        <FacturaManualDialog servicio={servicioElegido} cat={cat} onClose={() => setServicioElegido(null)} onRegistrada={registrada} />
      )}
    </DashboardLayout>
  );
}
